import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, Video, Square, X, Pause, Play, Timer, Clock3, Check, Loader2, RotateCcw, Sparkles, Gauge, Scissors, Type, Smile, Wand2 } from "lucide-react";

// Phase 2 — Color filters (CSS-based, applied during preview AND on playback in feed)
const FILTERS = [
  { id: "none",     name: "Original", css: "none" },
  { id: "vivid",    name: "Vivid",    css: "saturate(1.5) contrast(1.15)" },
  { id: "warm",     name: "Warm",     css: "sepia(0.25) saturate(1.2) hue-rotate(-10deg)" },
  { id: "cool",     name: "Cool",     css: "saturate(1.1) hue-rotate(180deg) brightness(1.05) contrast(1.05)" },
  { id: "bw",       name: "B&W",      css: "grayscale(1) contrast(1.05)" },
  { id: "vintage",  name: "Vintage",  css: "sepia(0.4) saturate(1.15) contrast(0.95) brightness(1.05)" },
  { id: "cinema",   name: "Cinema",   css: "saturate(0.85) contrast(1.2) brightness(0.95) hue-rotate(-5deg)" },
];

// Phase 3 — sticker library
const STICKERS = ["❤️", "🔥", "✨", "🎉", "💯", "👀", "😂", "🌟", "💖", "🤩", "🎵", "🎬"];

// Phase 4 — Lens: extensive beauty + face masks (parity with Live.jsx)
const BEAUTY_PRESETS = [
  { id: "off",         name: "Off",         css: "" },
  { id: "soft",        name: "Soft",        css: "blur(0.3px) brightness(1.05) contrast(0.98) saturate(1.05)" },
  { id: "glow",        name: "Glow",        css: "brightness(1.10) contrast(1.02) saturate(1.15)" },
  { id: "smooth",      name: "Smooth",      css: "blur(0.6px) brightness(1.08) contrast(0.97) saturate(1.08)" },
  { id: "porcelain",   name: "Porcelain",   css: "blur(0.5px) brightness(1.12) contrast(0.95) saturate(1.05)" },
  { id: "hd_soft",     name: "HD Soft",     css: "blur(0.4px) brightness(1.09) contrast(1.03) saturate(1.10)" },
  { id: "bright",      name: "Bright",      css: "brightness(1.18) contrast(1.00) saturate(1.12)" },
  { id: "pink_glow",   name: "Pink Glow",   css: "brightness(1.08) contrast(1.02) saturate(1.18) hue-rotate(-8deg) sepia(0.12)" },
  { id: "moon",        name: "Moonlight",   css: "brightness(1.06) contrast(0.98) saturate(0.90) hue-rotate(200deg)" },
  { id: "peach",       name: "Peach",       css: "brightness(1.10) contrast(1.00) saturate(1.20) sepia(0.15) hue-rotate(-5deg)" },
  { id: "cream",       name: "Cream",       css: "brightness(1.08) contrast(0.96) saturate(1.05) sepia(0.20)" },
  { id: "clarity",     name: "Clarity",     css: "brightness(1.06) contrast(1.10) saturate(1.20)" },
  { id: "rose",        name: "Rose",        css: "brightness(1.06) contrast(1.02) saturate(1.35) hue-rotate(-15deg) sepia(0.10)" },
  { id: "cool_white",  name: "Cool White",  css: "brightness(1.10) contrast(1.05) saturate(1.05) hue-rotate(180deg) invert(0.02)" },
  { id: "hollywood",   name: "Hollywood",   css: "brightness(1.05) contrast(1.15) saturate(0.90) sepia(0.20)" },
  { id: "korean",      name: "K-Beauty",    css: "blur(0.5px) brightness(1.13) contrast(0.97) saturate(1.08) sepia(0.06)" },
  { id: "diva",        name: "Diva",        css: "brightness(1.05) contrast(1.20) saturate(1.30)" },
  { id: "bollywood",   name: "Bollywood",   css: "brightness(1.10) contrast(1.10) saturate(1.35) sepia(0.10) hue-rotate(-8deg)" },
  { id: "vlogger",     name: "Vlogger",     css: "brightness(1.08) contrast(1.05) saturate(1.15)" },
  { id: "night",       name: "Night Mode",  css: "brightness(1.30) contrast(1.15) saturate(1.10)" },
];
const FACE_MASKS = [
  "🕶", "👓", "🎩", "👑", "🎓", "🧢", "👒", "⛑", "🪖", "🎪",
  "🐶", "🐱", "🐰", "🦊", "🐻", "🐼", "🐨", "🦁", "🐯", "🐸",
  "🐵", "🐷", "🐮", "🦄", "🐲", "😷", "🥸", "🎭", "👺", "👻",
  "🌟", "✨", "💫", "🔥", "❤️", "💖", "💯", "😂", "🥰", "😎",
  "👼", "😇", "🤡", "👹", "👽", "🤖", "💀", "☠️", "🎃", "🍭",
];

/**
 * CameraRecorder — In-browser reel recording component.
 * Features: front/back camera toggle, timer countdown (3/5/10s),
 * length cap (15/30/60/90s with auto-stop), pause/resume, retake, preview.
 *
 * Props:
 *  - open (bool)
 *  - onClose()
 *  - onDone(file: File)   // called with the recorded webm/mp4 File when user confirms
 */
export default function CameraRecorder({ open, onClose, onDone }) {
  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const [facing, setFacing] = useState("user"); // 'user' | 'environment'
  const [camReady, setCamReady] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [phase, setPhase] = useState("ready"); // ready | countdown | recording | paused | preview
  const [countdown, setCountdown] = useState(0);
  const [countdownChoice, setCountdownChoice] = useState(0); // 0 | 3 | 5 | 10
  const [maxSeconds, setMaxSeconds] = useState(60); // 15 | 30 | 60 | 90
  const [elapsed, setElapsed] = useState(0); // recorded seconds
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // -------- Phase 2 — Editor state (filter/speed/trim) --------
  const [filterId, setFilterId] = useState("none");
  const [beautyId, setBeautyId] = useState("off");
  const [speed, setSpeed] = useState(1);
  const [duration, setDuration] = useState(0); // recorded duration (sec)
  const [trim, setTrim] = useState({ start: 0, end: 0 }); // seconds
  // -------- Phase 3 — Overlays state (text + stickers) --------
  const [overlays, setOverlays] = useState([]); // [{type:'text'|'sticker', text, x, y, color, fontSize, id}]
  const [editorPanel, setEditorPanel] = useState("filter"); // filter | lens | speed | trim | text | sticker
  const [textDraft, setTextDraft] = useState("");
  const draggingRef = useRef(null);

  const rawFilterCss = (FILTERS.find(f => f.id === filterId) || FILTERS[0]).css;
  const beautyCss = (BEAUTY_PRESETS.find(b => b.id === beautyId) || BEAUTY_PRESETS[0]).css;
  const filterCss = beautyCss ? `${rawFilterCss === "none" ? "" : rawFilterCss} ${beautyCss}`.trim() : rawFilterCss;

  // Apply speed and trim to the preview player whenever they change
  useEffect(() => {
    const v = previewVideoRef.current;
    if (!v) return;
    v.playbackRate = speed;
    if (trim.end > trim.start) {
      if (v.currentTime < trim.start || v.currentTime > trim.end) v.currentTime = trim.start;
      const onTime = () => { if (v.currentTime >= trim.end) v.currentTime = trim.start; };
      v.addEventListener("timeupdate", onTime);
      return () => v.removeEventListener("timeupdate", onTime);
    }
  }, [speed, trim.start, trim.end, phase, previewUrl]);

  const addText = () => {
    if (!textDraft.trim()) return;
    setOverlays(o => [...o, {
      id: Math.random().toString(36).slice(2),
      type: "text", text: textDraft.slice(0, 60),
      x: 50, y: 50, color: "#FFD700", fontSize: 32,
    }]);
    setTextDraft("");
  };
  const addSticker = (s) => setOverlays(o => [...o, {
    id: Math.random().toString(36).slice(2),
    type: "sticker", text: s, x: 50, y: 50, fontSize: 48,
  }]);
  const removeOverlay = (id) => setOverlays(o => o.filter(x => x.id !== id));
  const onOverlayDrag = (id, dx, dy, rect) => {
    setOverlays(o => o.map(x => x.id === id ? {
      ...x,
      x: Math.max(0, Math.min(100, x.x + (dx / rect.width) * 100)),
      y: Math.max(0, Math.min(100, x.y + (dy / rect.height) * 100)),
    } : x));
  };

  // -------- Camera lifecycle --------
  const startCamera = async () => {
    setCamErr("");
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCamReady(true);
    } catch (e) {
      setCamErr(e?.message || "Camera access denied. Allow camera + mic permissions and reload.");
      setCamReady(false);
    }
  };

  const stopCamera = () => {
    try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch (_e) { /* noop */ }
    streamRef.current = null;
    setCamReady(false);
  };

  useEffect(() => {
    if (!open) return;
    startCamera();
    return () => {
      stopRecorderHard();
      stopCamera();
      clearInterval(tickRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [open, facing]);

  // -------- Recording --------
  const pickMime = () => {
    const opts = ["video/mp4;codecs=avc1,mp4a", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    for (const m of opts) {
      if (window.MediaRecorder?.isTypeSupported?.(m)) return m;
    }
    return "";
  };

  const beginCountdownThenRecord = () => {
    if (!streamRef.current) return;
    if (countdownChoice > 0) {
      setPhase("countdown");
      setCountdown(countdownChoice);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            actuallyStartRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      actuallyStartRecording();
    }
  };

  const actuallyStartRecording = () => {
    try {
      chunksRef.current = [];
      const mime = pickMime();
      const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : {});
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = handleStop;
      rec.start(250);
      recorderRef.current = rec;
      setPhase("recording");
      setElapsed(0);
      clearInterval(tickRef.current);
      tickRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 0.1;
          if (next >= maxSeconds) {
            stopRecorder();
            return maxSeconds;
          }
          return next;
        });
      }, 100);
    } catch (e) {
      setCamErr("Could not start recording: " + (e?.message || e));
      setPhase("ready");
    }
  };

  const togglePause = () => {
    if (!recorderRef.current) return;
    if (phase === "recording") {
      recorderRef.current.pause();
      clearInterval(tickRef.current);
      setPhase("paused");
    } else if (phase === "paused") {
      recorderRef.current.resume();
      tickRef.current = setInterval(() => {
        setElapsed(prev => {
          const next = prev + 0.1;
          if (next >= maxSeconds) { stopRecorder(); return maxSeconds; }
          return next;
        });
      }, 100);
      setPhase("recording");
    }
  };

  const stopRecorder = () => {
    try { if (recorderRef.current?.state !== "inactive") recorderRef.current?.stop(); } catch (_e) { /* noop */ }
    clearInterval(tickRef.current);
  };

  const stopRecorderHard = () => {
    try { if (recorderRef.current?.state && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (_e) { /* noop */ }
    clearInterval(tickRef.current);
    recorderRef.current = null;
  };

  const handleStop = () => {
    const mime = recorderRef.current?.mimeType || "video/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    if (blob.size === 0) {
      setCamErr("Recording was empty — please try again.");
      setPhase("ready");
      return;
    }
    const url = URL.createObjectURL(blob);
    setPreviewBlob(blob);
    setPreviewUrl(url);
    setPhase("preview");
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setPreviewBlob(null); setElapsed(0);
    setPhase("ready");
    if (!camReady) startCamera();
  };

  const confirm = async () => {
    if (!previewBlob) return;
    setSubmitting(true);
    const ext = previewBlob.type.includes("mp4") ? "mp4" : "webm";
    const file = new File([previewBlob], `reel_${Date.now()}.${ext}`, { type: previewBlob.type });
    // Carry editor edits alongside the file so UploadReel can persist them on the reel
    const edits = {
      filter_id: filterId,
      filter_css: filterCss,
      speed,
      trim: { start: trim.start, end: trim.end || duration },
      overlays,
    };
    onDone?.(file, edits);
    setSubmitting(false);
    handleClose();
  };

  const handleClose = () => {
    stopRecorderHard();
    stopCamera();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setPreviewBlob(null); setElapsed(0); setPhase("ready"); setCountdown(0);
    setFilterId("none"); setSpeed(1); setTrim({ start: 0, end: 0 }); setOverlays([]); setEditorPanel("filter");
    onClose?.();
  };

  if (!open) return null;

  const progressPct = Math.min(100, (elapsed / maxSeconds) * 100);

  return (
    <div className="fixed inset-0 z-[70] bg-black" data-testid="camera-recorder">
      {/* Header — floating over camera */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-2 bg-gradient-to-b from-black/80 to-transparent">
        <button onClick={handleClose} data-testid="recorder-close" className="p-2 rounded-full bg-white/10 active:scale-90">
          <X className="w-4 h-4"/>
        </button>
        <div className="flex-1 text-center text-sm font-bold">
          {phase === "ready" && "Record Reel"}
          {phase === "countdown" && "Get ready…"}
          {phase === "recording" && <span className="text-[#FF2A5F]">● Recording {elapsed.toFixed(1)}s / {maxSeconds}s</span>}
          {phase === "paused" && <span className="text-[#FF7A00]">⏸ Paused {elapsed.toFixed(1)}s / {maxSeconds}s</span>}
          {phase === "preview" && "Review your reel"}
        </div>
        {phase === "ready" && (
          <button onClick={() => setFacing(f => f === "user" ? "environment" : "user")}
            data-testid="recorder-flip" className="p-2 rounded-full bg-white/10 active:scale-90">
            <RefreshCw className="w-4 h-4"/>
          </button>
        )}
        {phase !== "ready" && <div className="w-8"/>}
      </div>

      {/* Camera preview / playback — TRUE full-screen */}
      <div className="absolute inset-0 bg-black overflow-hidden">
        {phase !== "preview" && (
          <video
            ref={videoRef}
            data-testid="recorder-camera-video"
            autoPlay
            playsInline
            muted
            className={`absolute inset-0 w-full h-full object-cover ${facing === "user" ? "scale-x-[-1]" : ""}`}
          />
        )}
        {phase === "preview" && previewUrl && (
          <video
            ref={previewVideoRef}
            data-testid="recorder-preview-video"
            src={previewUrl}
            autoPlay
            playsInline
            controls={false}
            loop
            onLoadedMetadata={(e) => {
              const d = Math.max(0.1, e.currentTarget.duration || 0);
              setDuration(d);
              if (!trim.end) setTrim(t => ({ ...t, end: d }));
            }}
            className="absolute inset-0 w-full h-full object-contain bg-black"
            style={{ filter: filterCss }}
          />
        )}

        {/* Phase 3 — Overlays rendered on top of preview */}
        {phase === "preview" && overlays.map(o => (
          <div
            key={o.id}
            data-testid={`overlay-${o.id}`}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              draggingRef.current = { id: o.id, lastX: e.clientX, lastY: e.clientY };
            }}
            onPointerMove={(e) => {
              const drag = draggingRef.current;
              if (!drag || drag.id !== o.id) return;
              const rect = e.currentTarget.parentElement.getBoundingClientRect();
              const dx = e.clientX - drag.lastX, dy = e.clientY - drag.lastY;
              draggingRef.current = { id: o.id, lastX: e.clientX, lastY: e.clientY };
              onOverlayDrag(o.id, dx, dy, rect);
            }}
            onPointerUp={() => { draggingRef.current = null; }}
            className="absolute select-none cursor-move font-extrabold drop-shadow-lg"
            style={{
              left: `${o.x}%`, top: `${o.y}%`,
              transform: "translate(-50%, -50%)",
              color: o.color, fontSize: o.fontSize, lineHeight: 1,
              textShadow: o.type === "text" ? "0 2px 12px rgba(0,0,0,0.85)" : "none",
              touchAction: "none",
            }}
          >
            {o.text}
            <button onClick={() => removeOverlay(o.id)} className="absolute -top-2 -right-3 w-4 h-4 rounded-full bg-[#FF2A5F] text-white text-[10px] flex items-center justify-center">×</button>
          </div>
        ))}

        {/* Camera error overlay */}
        {camErr && phase !== "preview" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-center p-6" data-testid="recorder-err">
            <Camera className="w-10 h-10 text-[#FF2A5F] mb-3"/>
            <div className="font-bold text-sm mb-1">Camera unavailable</div>
            <div className="text-xs text-white/70 mb-4">{camErr}</div>
            <button onClick={startCamera} className="px-4 py-2 rounded-full bg-[#A855F7] font-bold text-sm">Try again</button>
          </div>
        )}

        {/* Big countdown number */}
        {phase === "countdown" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div data-testid="recorder-countdown" className="text-9xl font-black text-white drop-shadow-2xl animate-pulse">{countdown}</div>
          </div>
        )}

        {/* Progress bar */}
        {(phase === "recording" || phase === "paused") && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
            <div data-testid="recorder-progress" className="h-full bg-gradient-to-r from-[#FF2A5F] to-[#FF7A00]" style={{ width: `${progressPct}%` }}/>
          </div>
        )}
      </div>

      {/* Controls — Right-side vertical toolbar (editor panel + settings) */}
      <div className="absolute top-14 right-3 bottom-32 z-20 w-[86px] flex flex-col items-stretch gap-2" data-testid="recorder-right-toolbar">
        {/* Phase 2/3 Editor tabs (preview only) */}
        {phase === "preview" && (
          <div className="flex flex-col gap-1.5" data-testid="editor-tabs">
            {[
              { id: "filter",  icon: Sparkles, label: "Filter" },
              { id: "lens",    icon: Wand2,    label: "Lens" },
              { id: "speed",   icon: Gauge,    label: "Speed" },
              { id: "trim",    icon: Scissors, label: "Trim" },
              { id: "text",    icon: Type,     label: "Text" },
              { id: "sticker", icon: Smile,    label: "Sticker" },
            ].map(t => {
              const Ic = t.icon;
              const active = editorPanel === t.id;
              return (
                <button key={t.id} type="button" onClick={() => setEditorPanel(t.id)}
                  data-testid={`editor-tab-${t.id}`}
                  className={`w-full flex flex-col items-center gap-0.5 px-1 py-2 rounded-xl text-[9px] font-bold active:scale-95 ${active ? "bg-[#A855F7] text-white" : "bg-black/50 backdrop-blur border border-white/10 text-white/80"}`}>
                  <Ic className="w-4 h-4"/>{t.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Ready-state right-side settings: length + timer */}
        {phase === "ready" && (
          <div className="flex flex-col gap-2" data-testid="recorder-settings">
            <div className="rounded-xl bg-black/55 backdrop-blur border border-white/10 p-2">
              <div className="text-[9px] font-bold text-white/60 flex items-center gap-1 mb-1"><Clock3 className="w-3 h-3"/>Length</div>
              <div className="flex flex-col gap-1">
                {[15, 30, 60, 90].map(s => (
                  <button key={s} onClick={() => setMaxSeconds(s)}
                    data-testid={`recorder-length-${s}`}
                    className={`py-1 rounded-lg text-[10px] font-bold active:scale-95 ${maxSeconds === s ? "bg-[#FF7A00] text-black" : "bg-white/8 border border-white/10 text-white/80"}`}>
                    {s}s
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-black/55 backdrop-blur border border-white/10 p-2">
              <div className="text-[9px] font-bold text-white/60 flex items-center gap-1 mb-1"><Timer className="w-3 h-3"/>Timer</div>
              <div className="flex flex-col gap-1">
                {[0, 3, 5, 10].map(s => (
                  <button key={s} onClick={() => setCountdownChoice(s)}
                    data-testid={`recorder-timer-${s}`}
                    className={`py-1 rounded-lg text-[10px] font-bold active:scale-95 ${countdownChoice === s ? "bg-[#A855F7] text-white" : "bg-white/8 border border-white/10 text-white/80"}`}>
                    {s === 0 ? "Off" : `${s}s`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom control area — record button + editor panel body */}
      <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent pt-6 px-3 pb-4 space-y-3">

        {/* Phase 2/3 Editor panel body (preview only) — rendered below the right toolbar */}
        {phase === "preview" && (
          <div data-testid="editor-panel">
            {/* Editor panel body */}
            <div className="rounded-2xl bg-black/60 backdrop-blur border border-white/10 p-2.5 mb-3 max-h-[140px] overflow-y-auto">
              {editorPanel === "filter" && (
                <div className="flex gap-1.5 overflow-x-auto" data-testid="editor-filters">
                  {FILTERS.map(f => (
                    <button key={f.id} type="button" onClick={() => setFilterId(f.id)}
                      data-testid={`filter-${f.id}`}
                      className={`flex-shrink-0 px-3 py-2 rounded-xl text-[10px] font-bold active:scale-95 border ${filterId === f.id ? "bg-[#A855F7] text-white border-[#A855F7]" : "bg-white/5 text-white/80 border-white/10"}`}>
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
              {editorPanel === "speed" && (
                <div className="flex gap-1.5" data-testid="editor-speeds">
                  {[0.5, 1, 2].map(s => (
                    <button key={s} type="button" onClick={() => setSpeed(s)}
                      data-testid={`speed-${s}`}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold active:scale-95 ${speed === s ? "bg-[#FF7A00] text-black" : "bg-white/8 text-white/80"}`}>
                      {s}x
                    </button>
                  ))}
                </div>
              )}
              {editorPanel === "trim" && (
                <div data-testid="editor-trim" className="px-1">
                  <div className="text-[10px] text-white/60 mb-1 flex justify-between">
                    <span>Start: {trim.start.toFixed(1)}s</span>
                    <span>End: {(trim.end || duration).toFixed(1)}s</span>
                  </div>
                  <div className="space-y-1.5">
                    <input data-testid="trim-start" type="range" min={0} max={duration} step={0.1} value={trim.start}
                      onChange={e => setTrim(t => ({ ...t, start: Math.min(parseFloat(e.target.value), (t.end || duration) - 0.5) }))}
                      className="w-full accent-[#FF2A5F]"/>
                    <input data-testid="trim-end" type="range" min={0} max={duration} step={0.1} value={trim.end || duration}
                      onChange={e => setTrim(t => ({ ...t, end: Math.max(parseFloat(e.target.value), t.start + 0.5) }))}
                      className="w-full accent-[#00E5FF]"/>
                  </div>
                </div>
              )}
              {editorPanel === "text" && (
                <div data-testid="editor-text" className="flex items-center gap-2">
                  <input value={textDraft} onChange={e => setTextDraft(e.target.value)} placeholder="Type text…"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addText(); } }}
                    data-testid="text-input"
                    className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
                  <button type="button" onClick={addText} data-testid="text-add"
                    className="px-3 py-2 rounded-xl bg-[#FF7A00] text-black font-bold text-xs active:scale-95">+ Add</button>
                </div>
              )}
              {editorPanel === "sticker" && (
                <div className="flex flex-wrap gap-1.5" data-testid="editor-stickers">
                  {STICKERS.map(s => (
                    <button key={s} type="button" onClick={() => addSticker(s)}
                      data-testid={`sticker-${s}`}
                      className="text-2xl p-1.5 rounded-lg bg-white/5 hover:bg-white/15 active:scale-90">{s}</button>
                  ))}
                </div>
              )}
              {editorPanel === "lens" && (
                <div data-testid="editor-lens" className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Beauty ({BEAUTY_PRESETS.length})</div>
                  <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="editor-beauty">
                    {BEAUTY_PRESETS.map(b => (
                      <button key={b.id} type="button" onClick={() => setBeautyId(b.id)}
                        data-testid={`beauty-${b.id}`}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-bold active:scale-95 border ${beautyId === b.id ? "bg-[#FF7A00] text-black border-[#FF7A00]" : "bg-white/5 text-white/85 border-white/10"}`}>
                        {b.name}
                      </button>
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold pt-1">Face Masks ({FACE_MASKS.length})</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="editor-face-masks">
                    {FACE_MASKS.map((s, i) => (
                      <button key={`${s}-${i}`} type="button" onClick={() => setOverlays(o => [...o, { id: Math.random().toString(36).slice(2), type: "sticker", text: s, x: 50, y: 30, fontSize: 96 }])}
                        data-testid={`facemask-${i}`}
                        className="text-2xl p-1.5 rounded-lg bg-white/5 hover:bg-white/15 active:scale-90">{s}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bottom record button row */}
        <div className="flex items-center justify-center gap-6 pt-2">
          {phase === "ready" && (
            <button onClick={beginCountdownThenRecord} disabled={!camReady}
              data-testid="recorder-record"
              className="w-16 h-16 rounded-full bg-[#FF2A5F] border-4 border-white flex items-center justify-center disabled:opacity-40 active:scale-90 shadow-lg shadow-[#FF2A5F]/40">
              <Video className="w-6 h-6 text-white"/>
            </button>
          )}
          {phase === "recording" && (
            <>
              <button onClick={togglePause} data-testid="recorder-pause"
                className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center active:scale-90">
                <Pause className="w-5 h-5"/>
              </button>
              <button onClick={stopRecorder} data-testid="recorder-stop"
                className="w-16 h-16 rounded-full bg-white border-4 border-[#FF2A5F] flex items-center justify-center active:scale-90">
                <Square className="w-6 h-6 text-[#FF2A5F] fill-[#FF2A5F]"/>
              </button>
              <div className="w-12"></div>
            </>
          )}
          {phase === "paused" && (
            <>
              <button onClick={togglePause} data-testid="recorder-resume"
                className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center active:scale-90">
                <Play className="w-5 h-5"/>
              </button>
              <button onClick={stopRecorder} data-testid="recorder-stop"
                className="w-16 h-16 rounded-full bg-white border-4 border-[#FF2A5F] flex items-center justify-center active:scale-90">
                <Check className="w-6 h-6 text-emerald-600"/>
              </button>
              <div className="w-12"></div>
            </>
          )}
          {phase === "preview" && (
            <>
              <button onClick={retake} data-testid="recorder-retake"
                className="px-5 py-3 rounded-full bg-white/10 border border-white/20 font-bold text-sm flex items-center gap-2 active:scale-95">
                <RotateCcw className="w-4 h-4"/>Retake
              </button>
              <button onClick={confirm} disabled={submitting} data-testid="recorder-use"
                className="px-6 py-3 rounded-full bg-gradient-to-r from-[#A855F7] to-[#FF2A5F] text-white font-bold text-sm flex items-center gap-2 active:scale-95 disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Check className="w-4 h-4"/>}
                Use this video
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
